type SessionUserData = {
  id?: number
  full_name?: string
  email?: string
  image?: string
  access_token?: string
  push_notification?: number
  [key: string]: unknown
}

type ApiResponse<T = unknown> = {
  success?: boolean
  status?: number
  message?: string
  data?: T
  [key: string]: unknown
}

const SESSION_CHANGED_EVENT = 'session-changed'

const SESSION_KEYS = {
  authToken: 'authorization',
  isLoggedIn: 'isLoggedIn',
  userData: 'userData',
  isNotification: 'isNotification',
  /** Firebase Cloud Messaging registration token for this browser session */
  fcmRegistrationToken: 'fcmRegistrationToken',
  isShowFreeTrialPopup: 'isShowFreeTrialPopup',
  isRememberMe: 'isRememberMe',
  email: 'email',
  password: 'password',
  appLanguage: 'app-language',
} as const

function emitSessionChanged() {
  window.dispatchEvent(new Event(SESSION_CHANGED_EVENT))
}

export function getAuthToken(): string {
  const token = localStorage.getItem(SESSION_KEYS.authToken) || ''
  console.log('[Session] getAuthToken called, token exists:', !!token)
  return token
}

export function isUserLoggedIn(): boolean {
  const isLoggedIn = localStorage.getItem(SESSION_KEYS.isLoggedIn) === 'true'
  const token = getAuthToken()
  const result = isLoggedIn && Boolean(token)
  console.log('[Session] isUserLoggedIn check:', { isLoggedIn, hasToken: !!token, result })
  return result
}

/** Single place to set the auth token (e.g. after login). Used for all API headers. */
export function setAuthToken(token: string): void {
  const t = token ? String(token).trim() : ''
  console.log('[Session] setAuthToken called, token length:', t.length, 'first 30 chars:', t.substring(0, 30))
  
  if (t) {
    localStorage.setItem(SESSION_KEYS.authToken, t)
  }
  localStorage.setItem(SESSION_KEYS.isLoggedIn, 'true')
  
  // Force a synchronous write by reading back (Chrome fix)
  const verified = localStorage.getItem(SESSION_KEYS.authToken)
  if (verified !== t) {
    console.error('[Session] ⚠️ Token verification failed after write!')
  } else {
    console.log('[Session] ✅ Token verified in localStorage')
  }
  
  emitSessionChanged()
}

export function getFcmRegistrationToken(): string {
  return localStorage.getItem(SESSION_KEYS.fcmRegistrationToken) ?? ''
}

export function setFcmRegistrationToken(token: string): void {
  const t = token ? String(token).trim() : ''
  if (t) {
    localStorage.setItem(SESSION_KEYS.fcmRegistrationToken, t)
  } else {
    localStorage.removeItem(SESSION_KEYS.fcmRegistrationToken)
  }
  emitSessionChanged()
}

function clearFcmRegistrationTokenStorage(): void {
  localStorage.removeItem(SESSION_KEYS.fcmRegistrationToken)
}

export function clearFcmRegistrationToken(): void {
  clearFcmRegistrationTokenStorage()
  emitSessionChanged()
}

/** Remove auth token from localStorage (e.g. on 401). All API calls use getAuthToken() so they will get nothing after this. */
export function clearAuthToken(): void {
  localStorage.removeItem(SESSION_KEYS.authToken)
  localStorage.setItem(SESSION_KEYS.isLoggedIn, 'false')
  clearFcmRegistrationTokenStorage()
  emitSessionChanged()
}

export function saveAuthSession(userData?: SessionUserData, fullResponse?: ApiResponse<unknown>) {
  console.log('[Session] saveAuthSession called with:', { userData, fullResponse })
  
  // Extract token from all possible locations in the response
  const token =
    (userData && typeof userData.access_token === 'string' ? userData.access_token : null) ??
    (userData && typeof (userData as { token?: string }).token === 'string' ? (userData as { token: string }).token : null) ??
    (fullResponse?.data && typeof (fullResponse.data as { access_token?: string }).access_token === 'string' 
      ? (fullResponse.data as { access_token: string }).access_token 
      : null) ??
    (fullResponse?.data && typeof (fullResponse.data as { token?: string }).token === 'string' 
      ? (fullResponse.data as { token: string }).token 
      : null) ??
    (fullResponse && typeof (fullResponse as { access_token?: string }).access_token === 'string' 
      ? (fullResponse as { access_token: string }).access_token 
      : null) ??
    (fullResponse && typeof (fullResponse as { token?: string }).token === 'string' 
      ? (fullResponse as { token: string }).token 
      : null)

  console.log('[Session] Token extraction results:', {
    'userData.access_token': userData?.access_token,
    'userData.token': (userData as any)?.token,
    'fullResponse.data.access_token': (fullResponse?.data as any)?.access_token,
    'fullResponse.data.token': (fullResponse?.data as any)?.token,
    'fullResponse.access_token': (fullResponse as any)?.access_token,
    'fullResponse.token': (fullResponse as any)?.token,
    'extracted token': token ? token.substring(0, 30) + '...' : 'NONE'
  })

  if (token && String(token).trim()) {
    setAuthToken(String(token).trim())
    console.log('[Session] Token saved from login response')
  } else {
    localStorage.setItem(SESSION_KEYS.isLoggedIn, 'true')
    console.warn('[Session] ⚠️ NO TOKEN FOUND in response! Logged in without token!')
  }
  
  localStorage.setItem(SESSION_KEYS.isShowFreeTrialPopup, 'false')
  if (typeof userData?.push_notification === 'number') {
    localStorage.setItem(SESSION_KEYS.isNotification, String(userData.push_notification))
  }
  if (userData) {
    localStorage.setItem(SESSION_KEYS.userData, JSON.stringify(userData))
  }
  emitSessionChanged()
}

export function getStoredUserData(): SessionUserData | null {
  try {
    const raw = localStorage.getItem(SESSION_KEYS.userData)
    if (!raw) return null
    return JSON.parse(raw) as SessionUserData
  } catch (_) {
    return null
  }
}

export function isNotificationEnabled(): boolean {
  const raw = localStorage.getItem(SESSION_KEYS.isNotification)
  return raw === '1' || raw === 'true'
}

export function setNotificationEnabled(enabled: boolean) {
  localStorage.setItem(SESSION_KEYS.isNotification, enabled ? '1' : '0')
}

const GAME_PROXY_COOKIE = 'game_auth_token'
const GAME_PROXY_COOKIE_MAX_AGE = 120

/** Set a short-lived cookie so the game proxy can send the token only in the Authorization header (Bearer), never in the URL. */
export function setGameProxyToken(token: string): void {
  if (!token) return
  document.cookie = `${GAME_PROXY_COOKIE}=${encodeURIComponent(token)}; path=/; max-age=${GAME_PROXY_COOKIE_MAX_AGE}; SameSite=Lax`
}

export function clearSessionOnLogout() {
  document.cookie = `${GAME_PROXY_COOKIE}=; path=/; max-age=0`
  clearAuthToken()
  const rememberMe = localStorage.getItem(SESSION_KEYS.isRememberMe)
  const email = localStorage.getItem(SESSION_KEYS.email)
  const password = localStorage.getItem(SESSION_KEYS.password)
  const language = localStorage.getItem(SESSION_KEYS.appLanguage)
  localStorage.removeItem(SESSION_KEYS.userData)
  localStorage.removeItem(SESSION_KEYS.isNotification)
  localStorage.removeItem(SESSION_KEYS.isShowFreeTrialPopup)
  if (rememberMe !== null) localStorage.setItem(SESSION_KEYS.isRememberMe, rememberMe)
  if (email !== null) localStorage.setItem(SESSION_KEYS.email, email)
  if (password !== null) localStorage.setItem(SESSION_KEYS.password, password)
  if (language !== null) localStorage.setItem(SESSION_KEYS.appLanguage, language)
  emitSessionChanged()
}

export { SESSION_CHANGED_EVENT, SESSION_KEYS }
